export interface FormatHintMeta {
  description: string;
  example: string;
}

export interface VariableMeta {
  label: string;
  description: string;
}

export interface NamingConfig {
  variables: {
    insight: Record<string, VariableMeta>;
    db: Record<string, VariableMeta>;
  };
  format_hints: Record<string, Record<string, FormatHintMeta>>;
}

export interface TemplateToken {
  id: string;
  variable: string;
  hint?: string;
}

let nextTokenId = 0;
export const createToken = (variable: string, hint?: string): TemplateToken => {
  nextTokenId += 1;
  return { id: `tok-${nextTokenId}-${variable}`, variable, hint };
};

export const createStableToken = (variable: string, hint: string | undefined, index: number): TemplateToken => ({
  id: `tok-${index}-${variable}`,
  variable,
  hint,
});

export interface FirmTemplate {
  document_type: string;
  template_string: string | null;
  is_firm_override: boolean;
  origin: string | null;
  label: string;
}

// Preferred display order for variable chips
export const VARIABLE_ORDER = [
  'extended_type', 'year', 'month', 'issuer',
  'account_name', 'contact_name',
];

// Hidden from the UI for initial rollout — enable when per-type templates are supported
export const SUPPRESSED_VARIABLES = new Set([
  'doc_type', 'subtype', 'upload_year', 'upload_month',
]);

// Suggested starter templates — createToken called at import time for stable IDs
export const SUGGESTED_TEMPLATES: TemplateToken[][] = [
  [createToken('year'), createToken('extended_type'), createToken('issuer')],
  [createToken('account_name'), createToken('extended_type'), createToken('year')],
  [createToken('contact_name', 'initials'), createToken('extended_type'), createToken('issuer'), createToken('year')],
  [createToken('year'), createToken('month', 'Mon'), createToken('extended_type'), createToken('account_name', 'first')],
];

export const FIRM_DEFAULT_TYPE = '__all__';

// --- Separator support ---

export const SEPARATOR_OPTIONS = [
  { value: ' ', label: 'Space', display: ' ' },
  { value: '-', label: 'Dash', display: '–' },
  { value: '_', label: 'Underscore', display: '_' },
  { value: '.', label: 'Period', display: '.' },
] as const;

export const DEFAULT_SEPARATOR = ' ';

export interface ParsedTemplate {
  tokens: TemplateToken[];
  separators: string[]; // length = max(0, tokens.length - 1)
}

export const parseTemplateString = (template: string): ParsedTemplate => {
  const regex = /\{([^}]+)\}/g;
  const tokens: TemplateToken[] = [];
  const separators: string[] = [];
  let lastEnd = 0;

  Array.from(template.matchAll(regex)).forEach((match, index) => {
    if (tokens.length > 0) {
      separators.push(template.slice(lastEnd, match.index));
    }
    const inner = match[1];
    const colonIdx = inner.indexOf(':');
    if (colonIdx > 0) {
      tokens.push(createStableToken(inner.slice(0, colonIdx), inner.slice(colonIdx + 1), index));
    } else {
      tokens.push(createStableToken(inner, undefined, index));
    }
    lastEnd = (match.index ?? 0) + match[0].length;
  });

  return { tokens, separators };
};

export const serializeTokens = (tokens: TemplateToken[], separators: string[]): string =>
  tokens
    .map((t, i) => {
      const s = `{${t.variable}${t.hint ? `:${t.hint}` : ''}}`;
      return i < tokens.length - 1 ? s + (separators[i] ?? DEFAULT_SEPARATOR) : s;
    })
    .join('');

// Matches backend TemplateRenderer::SANITIZE_CHARS — characters unsafe in filenames
const SANITIZE_CHARS = /[",:;<>|?*]/g;

export const buildPreview = (
  tokens: TemplateToken[],
  separators: string[],
  documentValues?: Record<string, string> | null,
): string => {
  if (tokens.length === 0) return '';
  return tokens
    .map((t, i) => {
      const key = `${t.variable}${t.hint ? `:${t.hint}` : ''}`;
      const resolved = documentValues?.[key] ?? documentValues?.[t.variable];
      const value = resolved != null
        ? resolved.replace(SANITIZE_CHARS, '')
        : `{${key}}`;
      return i < tokens.length - 1 ? value + (separators[i] ?? DEFAULT_SEPARATOR) : value;
    })
    .join('')
    .trim();
};
